import PropTypes from 'prop-types';
import { graphql } from 'gatsby';

import Page from '../components/Page';
import HeroBanner from '../components/HeroBanner';
import JumpLinks from '../components/JumpLinks';
import Banner from '../components/Banner';
import SignUpSteps from '../components/SignUpSteps';
import BannerContent from '../components/BannerContent';
import CTABanner from '../components/CTABanner';
import InsuranceCoverage from '../components/InsuranceCoverage';
import FAQInsuranceAccordion from '../components/FAQ/FAQInsuranceAccordion';
import LocationModule from '../components/LocationModule';
import CardCarousel from '../components/CardCarousel';
import HomeAutoAccordions from '../components/HomeAutoAccordions';
import { getProvinceCode } from '../utils/regionLockedContent';

const replaceAllText = (data, find, replace) => {
  const stack = [data];
  while (stack?.length > 0) {
    const current = stack.pop();
    Object.entries(current).forEach(([key, value]) => {
      if (typeof value === 'string') current[key] = value.replaceAll(find, replace);
      else if (typeof value === 'object' && value !== null) stack.push(value);
    });
  }
  return data;
};

const RegionalPage = ({
  data: { contentfulRegionalPage },
  pageContext: { region },
}) => {
  const updatedContentfulRegionalPage = replaceAllText(contentfulRegionalPage, '(LOCATION)', region.name);
  const { sections, breadCrumbs } = updatedContentfulRegionalPage;

  return (
    <Page data={contentfulRegionalPage} provinceOverride={getProvinceCode(region)} showBackToTop>
      {sections.map((section) => {
        switch (section?.internal?.type) {
          case 'ContentfulHeroBannerSection':
            return (
              <HeroBanner
                key={section.id}
                data={section}
                variant="with-bread-crumbs"
                breadcrumbs={breadCrumbs}
                version="heroBannerV2"
              />
            );
          case 'ContentfulCombinatorSection':
            switch (section?.component) {
              case 'jump-links':
                return (
                  <JumpLinks
                    key={section.id}
                    data={section}
                    variant="top-links"
                    sticky
                  />
                );
              case 'locations-shared':
                return <LocationModule key={section.id} data={section} />;
              case 'regional-howtobuy':
                return <SignUpSteps key={section.id} data={section} />;
              case 'card-carousel':
                return (
                  <CardCarousel
                    key={section.id}
                    data={section}
                    isRegionLocked
                    useProvinceOverride
                  />
                );
              case null:
                switch (section.sectionId) {
                  case 'SaveAndEarn':
                    return <BannerContent key={section.id} data={section} />;
                  default:
                    return null;
                }
              default:
                return null;
            }
          case 'ContentfulTextSection':
            return (
              <CTABanner
                key={section.id}
                data={section}
              />
            );
          case 'ContentfulImageGallerySection':
            switch (section?.component) {
              case "ImageRight-GreyBG":
                return <Banner key={section.id} variant="large" data={section} />;
              case "ImageRight-DefaultBG":
                return <Banner key={section.id} variant="large" data={section} />;
              default:
                return null;
            }
          case 'ContentfulIconItemSection':
            return <InsuranceCoverage key={section.id} data={section} />;
          case 'ContentfulTextMultiParagraphSection':
            switch (section?.component) {
              case 'HomeAutoAccordions':
                return (
                  <HomeAutoAccordions
                    key={section.id}
                    data={section}
                  />
                );
              default:
                return null;
            }
          case 'ContentfulFaqSection':
            return <FAQInsuranceAccordion key={section.id} data={section} />;
          default:
            return null;
        }
      })}
    </Page>
  );
};

RegionalPage.propTypes = {
  data: PropTypes.shape().isRequired,
  pageContext: PropTypes.shape({
    region: PropTypes.shape({
      name: PropTypes.string.isRequired,
      pageSlug: PropTypes.string.isRequired,
      parentRegion: PropTypes.shape({
        name: PropTypes.string.isRequired,
        pageSlug: PropTypes.string.isRequired,
      }),
    }).isRequired,
  }).isRequired,
};

export default RegionalPage;

export const query = graphql`
  query RegionalPage($locale: String = "en-CA", $contentful_id: String) {
    contentfulRegionalPage(
      contentful_id: { eq: $contentful_id }
      node_locale: { eq: $locale }
    ) {
      pageType
      canLanguageToggle
      pageName
      metaDescription {
        metaDescription
      }
      topNotificationBanner {
        ... on ContentfulNotificationBanner {
          id
          bannerTitle
          bannerCount
          isFixed
          isDismissible
          color
          bannerContent {
            raw
          }
        }
      }
      optionalCustomPageHeader {
        ...HeaderData
      }
      hidePage
      optionalLegal {
        ...TextSection
      }
      breadCrumbs {
        linkText
        linkUrl
      }
      sections {
        ...HeroBanner
        ...CombinatorSection
        ...ProvidersSection
        ...ImageGallerySection
        ...TextSection
        ...IconItemSection
        ...FaqSection
        ...LinkSection
        ...TextMultiParagraphSection
      }
    }
  }
`;
